<template>
  <div class="map-search">
    <div class="map-search__line">
      <up-input
        :prop-value="query"
        :placeholder="$t('map.searchPlaceholder')"
        @onInput="onInput"
      />
      <button
        v-if="query"
        type="button"
        class="map-search__clear"
        :aria-label="$t('map.clear')"
        @click="clear"
      >
        <i class="exu-x-close" />
      </button>
      <i
        v-else
        class="exu-search-1 map-search__icon"
      />
    </div>

    <ul
      v-if="query && isOpen"
      class="map-search__results"
    >
      <li v-if="!results.length">
        <span class="map-search__nothing">{{ $t('map.nothingFound') }}</span>
      </li>
      <li
        v-for="item in results"
        :key="item.key"
      >
        <button
          type="button"
          class="map-search__item"
          @click="pick(item)"
        >
          <span class="map-search__name">{{ item.name }}</span>
          <span class="map-search__stand">{{ $t('map.stand') }} {{ item.stand }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { normalizeStandNumber } from '@/views/pages/MapView/standState';

/** Длиннее показывать некуда, а короче — уже не найти нужное. */
const MAX_RESULTS = 20;

export default {
  name: 'MapSearchView',

  props: {
    // компании: название и номер стенда
    directory: {
      type: Array,
      default: () => [],
    },
    // номера всех стендов карты: по ним ищут стенд без компании
    standNumbers: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['select'],

  data() {
    return {
      query: '',
      isOpen: false,
    };
  },

  computed: {
    /**
     * Совпадения: сначала компании по названию и номеру, потом стенды,
     * за которыми никто не числится, — они на карте есть, и дойти до
     * них человек может хотеть не меньше.
     */
    results() {
      const raw = this.query.trim();
      if (!raw) return [];

      const byName = raw.toLowerCase();
      const byNumber = normalizeStandNumber(raw);

      const companies = this.directory
        .filter((company) => {
          const name = (company.name || '').toLowerCase();
          const stand = normalizeStandNumber(company.stand);
          return name.includes(byName) || (byNumber && stand.includes(byNumber));
        })
        .map((company) => ({
          key: `company-${company.ident}-${company.stand}`,
          name: company.name,
          stand: company.stand,
          company,
        }));

      const taken = new Set(companies.map((item) => normalizeStandNumber(item.stand)));

      const stands = byNumber
        ? this.standNumbers
          .filter((number) => {
            const normal = normalizeStandNumber(number);
            return normal.includes(byNumber) && !taken.has(normal);
          })
          .map((number) => ({
            key: `stand-${number}`,
            name: this.$t('map.emptyStand'),
            stand: number,
            company: null,
          }))
        : [];

      return [...companies, ...stands].slice(0, MAX_RESULTS);
    },
  },

  methods: {
    onInput(value) {
      this.query = value || '';
      this.isOpen = true;
    },

    clear() {
      this.query = '';
      this.isOpen = false;
    },

    pick(item) {
      this.isOpen = false;
      this.$emit('select', item);
    },
  },
};
</script>

<style lang="scss" scoped>
.map-search {
  position: relative;
  width: 100%;

  &__line {
    position: relative;
    display: flex;
    align-items: center;
  }

  // Поле у карты стоит поверх неё, поэтому ему нужна своя тень:
  // на светлом зале рамки одной мало
  &__line :deep(input.up-form-control) {
    padding-right: 44px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &__clear,
  &__icon {
    position: absolute;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    font-size: 20px;
    color: #AFB0B6;
  }

  &__clear {
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
  }

  &__icon {
    pointer-events: none;
  }

  &__results {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 40vh;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
    background: #FFFFFF;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    padding: 10px 12px;
    cursor: pointer;

    // Нейтральный серый, как у выбранной строки в `droplist` клиента
    &:hover,
    &:focus {
      background: #ebebeb;
    }
  }

  &__name {
    font-size: 0.95rem;
    line-height: 1.2em;
    color: #1c1c1c;
  }

  &__stand {
    font-size: 0.8rem;
    color: #6E6E7A;
  }

  &__nothing {
    display: block;
    padding: 10px 12px;
    font-size: 0.9rem;
    color: #6E6E7A;
  }
}
</style>
